import { getConfig } from './config';
import { escapeHtml } from './helpers';
import { BASE_PATH } from './state';
import { showFlash, showToast } from './toast';
import { setButtonBusy } from './button-busy';

const reviewsUrl = `${BASE_PATH}/ajax/reviews`;

function renderStars(rating: number, interactive = false): string {
  let html = '';
  for (let i = 1; i <= 5; i++) {
    const filled = i <= Math.round(rating);
    const cls = filled ? 'review-star filled' : 'review-star';
    if (interactive) {
      html += `<span class="${cls}" data-rating="${i}" style="cursor:pointer;font-size:1.4rem;color:${filled ? '#f59e0b' : '#ddd'};">★</span>`;
    } else {
      html += `<span style="color:${filled ? '#f59e0b' : '#ddd'};font-size:1rem;">★</span>`;
    }
  }
  return html;
}

export function renderAverageRating(avg: number, count: number): string {
  if (count === 0 || avg === 0) return '';
  return `<div class="product-rating" style="display:flex;align-items:center;gap:4px;margin-top:2px;">
    ${renderStars(avg)}
    <span style="font-size:0.8rem;color:var(--muted,#999);">${avg} (${count})</span>
  </div>`;
}

export type ReviewRow = {
  rating: number;
  user_login?: string;
  created_at?: string;
  text?: string;
  admin_reply?: string;
};

export function renderReviewItem(r: ReviewRow): string {
  let html = `<div class="review-item" style="border-bottom:1px solid var(--border,#eee);padding:0.75rem 0;">`;
  html += `<div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">${renderStars(r.rating)}`;
  html += `<span style="font-weight:600;">${escapeHtml(r.user_login || 'Покупатель')}</span>`;
  html += `<span style="color:var(--muted);font-size:0.8rem;">${escapeHtml(r.created_at || '')}</span></div>`;
  html += `<p style="margin:4px 0;">${escapeHtml(r.text || '')}</p>`;
  if (r.admin_reply) {
    html += `<div style="background:var(--bg-soft,#f9fafb);padding:8px 12px;border-radius:var(--radius-sm,4px);margin-top:6px;">`;
    html += `<strong>Ответ магазина:</strong> ${escapeHtml(r.admin_reply)}</div>`;
  }
  html += '</div>';
  return html;
}

function buildReviewsSectionHtml(
  productId: string,
  reviews: ReviewRow[],
  avg: number,
  total: number,
  isAuth: boolean,
  loadError?: string,
): string {
  let html = '<div class="reviews-section" style="margin-top:1.5rem;">';
  html += '<h3 style="margin-bottom:0.75rem;">Отзывы';
  if (total > 0) html += ` <span style="color:var(--muted);font-weight:normal;">${renderStars(avg)} ${avg} (${total})</span>`;
  html += '</h3>';

  if (loadError) {
    html += `<p style="color:var(--muted);">${escapeHtml(loadError)}</p>`;
  } else if (reviews.length > 0) {
    for (const r of reviews) {
      html += renderReviewItem(r);
    }
    if (total > reviews.length) {
      html += `<button class="btn btn-outline btn-sm" data-action="load-more-reviews" data-product-id="${escapeHtml(productId)}" data-offset="${reviews.length}" data-total-reviews="${total}" style="margin-top:0.75rem;">Показать ещё</button>`;
    }
  } else if (!loadError) {
    html += '<p style="color:var(--muted);">Отзывов пока нет.</p>';
  }

  if (isAuth) {
    html += `<div class="review-form" style="margin-top:1rem;padding-top:1rem;border-top:1px solid var(--border,#eee);">`;
    html += '<h4 style="margin-bottom:0.5rem;">Оставить отзыв</h4>';
    html += `<div class="review-stars-input" style="margin-bottom:0.5rem;">${renderStars(0, true)}</div>`;
    html += `<textarea class="review-text-input" placeholder="Ваш отзыв..." rows="3" style="width:100%;padding:8px;border:1px solid var(--border,#ddd);border-radius:var(--radius-sm,4px);resize:vertical;"></textarea>`;
    html += `<button class="btn btn-accent btn-sm review-submit-btn" data-product-id="${escapeHtml(productId)}" style="margin-top:0.5rem;">Отправить</button>`;
    html += '</div>';
  }

  html += '</div>';
  return html;
}

function bindReviewForm(container: HTMLElement, productId: string, csrfToken: string): void {
  let selectedRating = 0;
  container.querySelectorAll('.review-star[data-rating]').forEach((star) => {
    (star as HTMLElement).addEventListener('click', () => {
      selectedRating = parseInt((star as HTMLElement).dataset.rating || '0', 10);
      container.querySelectorAll('.review-star[data-rating]').forEach((s, idx) => {
        (s as HTMLElement).style.color = idx < selectedRating ? '#f59e0b' : '#ddd';
        (s as HTMLElement).classList.toggle('filled', idx < selectedRating);
      });
    });
  });

  const submitBtn = container.querySelector('.review-submit-btn');
  if (!submitBtn) return;

  submitBtn.addEventListener('click', () => {
    void (async () => {
    const textEl = container.querySelector('.review-text-input') as HTMLTextAreaElement;
    const text = textEl?.value.trim() || '';
    if (selectedRating < 1 || selectedRating > 5) { showToast('Выберите рейтинг'); return; }
    if (text.length < 3) { showToast('Напишите отзыв'); return; }

    const btn = submitBtn as HTMLButtonElement;
    setButtonBusy(btn, true);

    const body = new URLSearchParams({ product_id: productId, rating: String(selectedRating), text, csrf_token: csrfToken });
    try {
      const r = await fetch(reviewsUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      const d = await r.json() as { success?: boolean; error?: string };
      if (r.ok && d.success) {
        setButtonBusy(btn, false);
        btn.textContent = '✓ Отправлен на модерацию';
        textEl.value = '';
        selectedRating = 0;
      } else {
        showFlash(d.error || 'Ошибка', 'error');
        setButtonBusy(btn, false);
        btn.textContent = 'Отправить';
      }
    } catch {
      showFlash('Ошибка сети', 'error');
      setButtonBusy(btn, false);
      btn.textContent = 'Отправить';
    }
    })();
  });
}

export async function loadMoreReviews(button: HTMLElement): Promise<void> {
  const productId = button.dataset.productId;
  if (!productId) return;

  const offset = parseInt(button.dataset.offset || '0', 10);
  const totalReviews = parseInt(button.dataset.totalReviews || '0', 10);
  const btn = button as HTMLButtonElement;
  btn.disabled = true;

  try {
    const res = await fetch(
      `${reviewsUrl}?product_id=${encodeURIComponent(productId)}&limit=5&offset=${offset}`,
    );
    const data = await res.json() as {
      reviews?: ReviewRow[];
      totalReviews?: number;
      error?: string;
    };

    if (!res.ok || data.error) {
      showFlash(data.error || 'Не удалось загрузить отзывы', 'error');
      btn.disabled = false;
      return;
    }

    const batch = data.reviews || [];
    const total = data.totalReviews ?? totalReviews;
    const fragment = batch.map(renderReviewItem).join('');
    btn.insertAdjacentHTML('beforebegin', fragment);

    const newOffset = offset + batch.length;
    if (newOffset >= total || batch.length === 0) {
      btn.remove();
    } else {
      btn.dataset.offset = String(newOffset);
      btn.dataset.totalReviews = String(total);
      btn.disabled = false;
    }
  } catch {
    showFlash('Ошибка сети', 'error');
    btn.disabled = false;
  }
}

export async function initReviewsBlock(container: HTMLElement): Promise<void> {
  const productId = container.dataset.productId;
  if (!productId) return;

  const csrfToken = getConfig('csrfToken', '') as string;
  const isAuth = getConfig('isAuthenticated', false) as boolean;
  const modulesEnabled = getConfig('modulesEnabled') as Record<string, boolean> | undefined;
  if (modulesEnabled?.reviews === false) return;

  container.innerHTML = '<p style="color:var(--muted);">Загрузка отзывов...</p>';

  try {
    const res = await fetch(`${reviewsUrl}?product_id=${encodeURIComponent(productId)}&limit=5&offset=0`);
    const data = await res.json() as {
      reviews?: Array<{ rating: number; user_login?: string; created_at?: string; text?: string; admin_reply?: string }>;
      averageRating?: number;
      totalReviews?: number;
      error?: string;
    };

    const loadError = (!res.ok || data.error)
      ? (data.error || 'Не удалось загрузить отзывы')
      : undefined;

    const reviews = loadError ? [] : (data.reviews || []);
    const avg: number = loadError ? 0 : (data.averageRating || 0);
    const total: number = loadError ? 0 : (data.totalReviews || 0);

    container.innerHTML = buildReviewsSectionHtml(productId, reviews, avg, total, isAuth, loadError);
    bindReviewForm(container, productId, csrfToken);
  } catch {
    container.innerHTML = buildReviewsSectionHtml(
      productId,
      [],
      0,
      0,
      isAuth,
      'Не удалось загрузить отзывы',
    );
    bindReviewForm(container, productId, csrfToken);
  }
}
